/*
=========================================================
Muse - Vue Ant Design Dashboard - v1.0.0
=========================================================

Product Page: https://www.creative-tim.com/product/vue-ant-design-dashboard
Copyright 2021 Creative Tim (https://www.creative-tim.com)
Coded by Creative Tim

=========================================================
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 
*/

import Vue from 'vue'
import Antd from 'ant-design-vue';
import 'ant-design-vue/dist/antd.css';
import App from './App.vue'
import DefaultLayout from './layouts/Default.vue'
import DashboardLayout from './layouts/Dashboard.vue'
import DashboardRTLLayout from './layouts/DashboardRTL.vue'
import router from './router'
// import './plugins/click-away'

import './scss/app.scss';

// axios
import axios from 'axios'
import VueAxios from 'vue-axios'
import config from "./config.js"
// auth
import VueKeycloakJs from '@dsb-norge/vue-keycloak-js'
// moment
import moment from 'moment'
// vuex
import store from './store/index';
// report
import * as XLSX from 'xlsx';
// image select
import VueSelectImage from 'vue-select-image'
// apex chart
import VueApexCharts from 'vue-apexcharts'
// chart gauge
import VueGauge from "vue-gauge";
// print pdf
import VueHtmlToPaper from 'vue-html-to-paper';

Vue.use(Antd);

// Vue.config.productionTip = false

// Configure axios
axios.defaults.baseURL = config.apiUrl
Vue.use(VueAxios, axios)
Vue.use(VueSelectImage)
// import { extendMoment } from 'moment-range';

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

Vue.use(VueGauge)

// pdf-test
const options = {
  name: '_blank',
  specs: [
    'fullscreen=yes',
    'titlebar=yes',
    'scrollbars=yes'
  ],
  styles: [
    'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css'
  ],
  // timeout: 1000, // default timeout before the print window appears
  autoClose: true, // if false, the window will not close after printing
  // windowTitle: globalThis.document.title, // override the window title
}
Vue.use(VueHtmlToPaper, options);

// Vue.prototype.moment = extendMoment(moment)
Vue.prototype.moment = moment
// excle xls
Vue.prototype.XLSX = XLSX

// GA TAG Router
import VueGtag from 'vue-gtag';
Vue.use(VueGtag, {
  config: { id: 'UA-179956144-1' }
}, router);

// Adding template layouts to the vue components.
Vue.component("layout-default", DefaultLayout);
Vue.component("layout-dashboard", DashboardLayout);
Vue.component("layout-dashboard-rtl", DashboardRTLLayout);
import VueQRCodeComponent from 'vue-qrcode-component'
Vue.component('qr-code', VueQRCodeComponent)

// auth service for MSAL
import authService from './services/authService';

// token intercepter
// token determination logic
// Helper for Smart Authen scenarios
function getSmartAuthenToken(config) {
  if (config.url === '/staff/profile/me/' || config.url === '/smart-authen/check-in/by-qr/') {
    return `Bearer ${Vue.prototype.$keycloak.token}`;
  }
  if (config.url === '/hri/time_attendance/time_server/') {
    return 'Token b5d48ab87dcfbb180ebb1876c0afa2501978e6c6';
  }
  // return `Token 7890c32eb5e812748bb3bf902cefb70013a96f28`;
  return `Token 7d12c1c8c4542f1e0dce7add8859d41c938d4e3c`;
}

// Helper for Standard Authentication flow
async function getStandardAuthToken(requiresAuth) {
  // Check Keycloak
  if (Vue.prototype.$keycloak.token !== null) {
    console.log('[Auth] Using Keycloak Token');
    return `Bearer ${Vue.prototype.$keycloak.token}`;
  }

  // Check MSAL
  const msalToken = await authService.getAccessToken();
  if (msalToken) {
    console.log('[Auth] Using MSAL Token');
    return `Bearer ${msalToken}`;
  }

  // Check if route requires auth
  if (!requiresAuth) {
    console.log('[Auth] No session required for this route, proceeding without token');
    return '';
  }

  // Fallback - No valid session, redirect to login
  console.log('[Auth] No valid session found for protected route, redirecting to login', router.currentRoute.path);
  if (['/timescan', '/smartauthenapp'].includes(router.currentRoute.path)) {
    return 'Token b5d48ab87dcfbb180ebb1876c0afa2501978e6c6';
  } else {
    if (router.currentRoute.path !== '/login') {
      router.push('/login');
    }
  }
}

// Main logic for token determination
async function determineAuthToken(config) {

  // console.log('router.currentRoute.path', router.currentRoute.path);
  // console.log('config.url', config.url);

  const isSmartAuthen = ['/smartauthenapp', '/smartauthenqr'].includes(router.currentRoute.path);

  // 1. Time Server (Common Exception) 
  if (['/hri/time_attendance/time_server/', '/hri/time_attendance/new_sent_data/'].includes(config.url)) {
    if (!isSmartAuthen) {
      console.log('[Auth] Using Time Server Token');
      return `Token b5d48ab87dcfbb180ebb1876c0afa2501978e6c6`;
    }
  }

  // 2. Smart Authen Special Handling
  if (isSmartAuthen) {
    return getSmartAuthenToken(config);
  }

  // 3. Delegate to Standard Authentication Flow
  return getStandardAuthToken(router.currentRoute.meta.requiresAuth);
}

// token intercepter
function tokenInterceptor() {
  axios.interceptors.request.use(async config => {
    // Prevent overwriting if a custom Authorization header is already provided
    if (config.headers && config.headers.Authorization) {
      return config;
    }

    const token = await determineAuthToken(config);
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  }, error => {
    return Promise.reject(error)
  })
}


// Check if we are inside an MSAL popup
if (window.opener && window.opener !== window) {
  // Basic check intended for MSAL popup handling
  // We do NOT want to initialize Keycloak here as it might strip the hash
  console.log('[Auth] Running inside MSAL Popup - Skipping App Mount');
  // We can just stop execution here or render a simple message
  // return; // But this is module scope... we can't return
} else {
  // SSO has been cancelled, skip Keycloak initialization and mount directly
  Vue.prototype.$keycloak = { token: null, authenticated: false };
  globalThis._keycloak = Vue.prototype.$keycloak;

  tokenInterceptor();
  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount('#app');
}